import React, { useRef, useEffect, useState } from 'react';
import juiceImage from '../assets/juice.webp';
import dairyImage from '../assets/dairy.webp';
import bakeryImage from '../assets/bakery.webp';
import meatImage from '../assets/meat.webp';

const productImages = {
  'juice-beverage-ingredients': juiceImage,
  'dairy-ingredients-commodities': dairyImage,
  'bakery-confectionery-ingredients': bakeryImage,
  'meat-ingredients': meatImage
};

const ShapeDivider = () => (
  <div className="shape-divider">
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
      <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
    </svg>
  </div>
);

const Products = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const productsRef = useRef([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [content, setContent] = useState({
    title: 'Our Products',
    items: [
      {
        title: "Juice & Beverage Ingredients",
        description: "Authentic taste, reliable performance, and innovative options for juices, smoothies, and wellness drinks.",
        subItems: [
          "Fruit Concentrates & Purees",
          "NFC Juices (Not From Concentrate)",
          "IQF Fruits & Preparations",
          "Compounds & Emulsions",
          "Flavors & Colors",
          "Sweeteners (traditional & natural)",
          "Functional Ingredients (vitamins, minerals, plant extracts)",
          "Stabilizers & Hydrocolloids"
        ]
      },
      {
        title: "Dairy Ingredients & Commodities",
        description: "Safe, Halal-certified, and consistent solutions for traditional and modern dairy applications.",
        subItems: [
          "Skimmed & Whole Milk Powders",
          "Fat-Filled Milk Powder (FFMP)",
          "Whey & Demineralized Whey Powders",
          "Lactose",
          "Casein & Caseinates",
          "Butter & Anhydrous Milk Fat (AMF)",
          "Cheese Powders & Ingredients"
        ]
      },
      {
        title: "Bakery & Confectionery Ingredients",
        description: "Enhancing taste, texture, and creativity in breads, pastries, chocolates, and sweets.",
        subItems: [
          "Dairy Ingredients: milk powders, whey, butter, cheese powders",
          "Fats & Oils: shortenings, cocoa butter substitutes",
          "Sweeteners: sucrose, glucose syrup, maltodextrin, alternatives",
          "Cocoa & Chocolate Products",
          "Fruit Preparations & Fillings",
          "Starches, Emulsifiers & Stabilizers",
          "Flavors & Colors (natural & nature-identical)"
        ]
      },
      {
        title: "Meat Ingredients",
        description: "Functional and specialty solutions for texture, juiciness, and extended shelf life in meat and poultry.",
        subItems: [
          "Proteins (soy, milk, pea)",
          "Phosphates",
          "Hydrocolloids & Starches",
          "Seasonings & Marinades (customized to regional tastes)",
          "Natural & Smoke Flavors",
          "Antioxidants & Preservatives",
          "Coatings & Batters",
          "Functional Fats & Oils"
        ]
      }
    ]
  });

  const API_BASE = process.env.REACT_APP_API_BASE || (typeof window !== 'undefined' ? window.location.origin : '');

  // Helper function to generate data attribute from product title
  const generateDataAttribute = (title) => {
    const normalized = title.toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove special characters including parentheses
      .replace(/\s+/g, '-')    // Replace spaces with hyphens
      .trim();
    
    return normalized;
  };

  const toggleCard = (index) => {
    if (expandedCard === index) {
      setExpandedCard(null);
    } else {
      setExpandedCard(index);
    }
  };

  const handleMouseEnter = (index) => {
    setHoveredCard(index);
  };

  const handleMouseLeave = () => {
    setHoveredCard(null);
  };

  useEffect(() => {
    // Fetch content from API
    fetch(API_BASE + '/backend/api.php/content?slug=products')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        if (data && data.item) {
          setContent(prev => ({
            ...prev,
            title: data.item.title || prev.title,
            items: Array.isArray(data.item.json) ? data.item.json : prev.items
          }));
        }
      })
      .catch(() => {});
  }, [API_BASE]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (headingRef.current) observer.observe(headingRef.current);
    
    productsRef.current.forEach(el => {
      if (el) observer.observe(el);
    });

    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
      if (headingRef.current) observer.unobserve(headingRef.current);
      
      productsRef.current.forEach(el => {
        if (el) observer.unobserve(el);
      });
    };
  }, []);

  return (
    <section 
      id="products" 
      className="products-section" 
      ref={sectionRef}
      style={{
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, #f8fffe 0%, #f0f9ff 100%)',
        position: 'relative'
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <h2 
          className="section-title animate-on-scroll slide-in-left" 
          ref={headingRef}
          style={{
            textAlign: 'center',
            marginBottom: '4rem',
            fontSize: '2.5rem',
            color: '#5FA32E',
            fontWeight: '700'
          }}
        >
          {content.title}
        </h2>
        
        <div className="products-grid-container" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '2rem',
          position: 'relative',
          zIndex: 2
        }}>
          {content.items.map((product, index) => {
            const productTitle = product.title || product.name || `Product ${index + 1}`;
            const productDescription = product.description || product.desc || product.text || '';
            const dataAttribute = generateDataAttribute(productTitle);
            const backgroundImage = (product && (product.imageUrl || product.imageURL || product.image)) || productImages[dataAttribute];
            const isExpanded = expandedCard === index;
            const isHovered = hoveredCard === index;
            const shouldShowBackground = isExpanded || isHovered;
            
            return (
              <div 
                key={index}
                className={`product-card animate-on-scroll fade-in delay-${index % 3}`}
                data-product={dataAttribute}
                ref={el => productsRef.current[index] = el}
                style={{
                  background: shouldShowBackground && backgroundImage
                    ? `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.4)), url(${backgroundImage})`
                    : '#e1facf',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center center',
                  backgroundRepeat: 'no-repeat',
                  borderRadius: '16px',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.08)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: '1px solid rgba(122, 201, 67, 0.1)',
                  height: 'auto'
                }}
                onClick={() => toggleCard(index)}
                onMouseEnter={() => handleMouseEnter(index)}
                onMouseLeave={handleMouseLeave}
              >
                {/* Main card content */}
                <div 
                  className="product-main-content"
                  style={{
                    padding: '2.5rem',
                    position: 'relative',
                    zIndex: 2,
                    transition: 'all 0.3s ease',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  {/* Decorative element */}
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: '60px',
                    height: '60px',
                    background: 'linear-gradient(135deg, #7AC943, #5FA32E)',
                    borderRadius: '0 16px 0 60px',
                    opacity: shouldShowBackground ? '0.2' : '0.1'
                  }}></div>
                  
                  <h3 style={{
                    fontSize: '1.4rem',
                    fontWeight: '600',
                    color: shouldShowBackground ? 'white' : '#2d5016',
                    marginBottom: '1rem',
                    lineHeight: '1.3',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 2,
                    textShadow: shouldShowBackground ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none'
                  }}>
                    {productTitle}
                  </h3>
                  
                  <p style={{
                    fontSize: '1rem',
                    color: shouldShowBackground ? 'white' : '#6b7280',
                    lineHeight: '1.6',
                    margin: '0',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 2,
                    maxWidth: '90%',
                    textShadow: shouldShowBackground ? '1px 1px 2px rgba(0,0,0,0.8)' : 'none'
                  }}>
                    {productDescription}
                  </p>
                </div>
                
                {/* Dropdown content */}
                <div 
                  className="product-dropdown-content"
                  style={{
                    maxHeight: isExpanded ? '500px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                    background: 'rgba(255, 255, 255, 0)',
                    borderRadius: '0 0 16px 16px',
                    borderTop: isExpanded ? '1px solid rgba(122, 201, 67, 0.2)' : 'none',
                    position: 'relative',
                    zIndex: 3
                  }}
                >
                  <div style={{ padding: '1.5rem 2.5rem 2.5rem' }}>
                    <ul style={{
                      margin: '0',
                      padding: '0',
                      listStyle: 'none',
                      columns: '2',
                      columnGap: '2rem'
                    }}>
                      {product.subItems && product.subItems.map((subItem, subIndex) => (
                        <li key={subIndex} style={{
                          padding: '0.5rem 0',
                          borderBottom: subIndex < product.subItems.length - 1 ? '1px solid rgba(123, 201, 67, 0)' : 'none',
                          fontSize: '0.95rem',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          breakInside: 'avoid'
                        }}>
                          <span style={{
                            display: 'inline-block',
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#7AC943',
                            marginRight: '0.75rem',
                            flexShrink: 0
                          }}></span>
                          {subItem}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <ShapeDivider />
      
      <style>{`
        .animated {
          opacity: 1;
          transform: translateY(0);
        }
        
        .product-card {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        
        .delay-0 { transition-delay: 0s; }
        .delay-1 { transition-delay: 0.2s; }
        .delay-2 { transition-delay: 0.4s; }
        
        /* Responsive grid for different screen sizes */
        @media (max-width: 1024px) {
          .products-grid-container {
            grid-template-columns: 1fr !important;
            max-width: 600px;
            margin: 0 auto;
          }
        }
        
        @media (max-width: 768px) {
          .products-section {
            padding: 3rem 1rem !important;
          }
          
          .section-title {
            font-size: 2rem !important;
            margin-bottom: 2rem !important;
          }
          
          .product-main-content {
            padding: 2rem !important;
            min-height: 120px !important;
          }
          
          .product-dropdown-content ul {
            columns: 1 !important;
          }
        }
        
        @media (max-width: 480px) {
          .product-card h3 {
            font-size: 1.2rem !important;
          }
          
          .product-main-content {
            padding: 1.5rem !important;
          }
          
          .product-dropdown-content div {
            padding: 1rem 1.5rem 1.5rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Products;